import { combineLatest, from, of, type Observable } from 'rxjs';
import {
  catchError,
  distinctUntilChanged,
  map,
  shareReplay,
  startWith,
  switchMap,
} from 'rxjs/operators';
import { spreadMap } from '@ardoq/rxbeach';
import isEqual from 'lodash/isEqual';
import noop from 'lodash/noop';
import { AppModules } from 'appContainer/types';
import { selectedModule$ } from 'appContainer/selectedModule$';
import { reportNavigation$ } from 'report/navigation/reportNavigation$';
import { reports$ } from 'streams/reports/reports$';
import { dashboards$ } from 'streams/dashboards/dashboards$';
import { viewpointsNavigation$ } from 'viewpoints/navigation/viewpointsNavigation$';
import { isViewpointMode$ } from 'traversals/loadedGraph$';
import { loadedState$ } from 'loadedState/loadedState$';
import { traversalState$ } from 'streams/traversals$';
import { activeView$ } from 'streams/views/mainContent/activeView$';
import { metamodelNavigation$ } from 'metamodel/navigation/metamodelNavigation$';
import { metamodels$ } from 'streams/metamodels/metamodels$';
import { initiativePage$ } from 'transformations/initiativePage/initiativePage$';
import { futureStatePage$ } from 'transformations/futureStatePage/futureStatePage$';
import { location$ } from 'router/declarative';
import { componentOverviewPage$ } from 'appContainer/componentOverviewPage/componentOverviewPage$';
import type {
  FutureStateContext,
  InitiativeContext,
  TopLevelContext,
} from './types';
import {
  toReportContext,
  toDashboardContext,
  toViewpointBrowseContext,
  toWorkspaceContext,
  toMetamodelContext,
  toTransformationContext,
  toFutureStateContext,
  toTransformationsScope,
  toComponentContext,
  topLevel,
} from './contextMappers';
import type { TransformationsScope } from './contextMappers';

export const prefetchDashboardContextChunk = (): void => {
  import('dashboard/navigation/dashboardNavigation$').catch(noop);
};

const transformationsScope$ = location$.pipe(
  map(toTransformationsScope),
  distinctUntilChanged(isEqual)
);

export const toTransformationsScopeContext = (
  scope: TransformationsScope
): Observable<InitiativeContext | FutureStateContext | TopLevelContext> => {
  switch (scope.page) {
    case 'initiative':
      return initiativePage$.pipe(
        map(state => toTransformationContext(scope.initiativeId, state))
      );
    case 'future_state':
      return combineLatest([futureStatePage$, initiativePage$]).pipe(
        map(
          ([futureStateState, initiativeState]) =>
            toFutureStateContext(scope, futureStateState) ??
            toTransformationContext(scope.initiativeId, initiativeState)
        )
      );
    case 'none':
      return of(topLevel(AppModules.TRANSFORMATIONS));
  }
};

export const assistantContext$ = selectedModule$.pipe(
  switchMap(({ selectedModule }) => {
    switch (selectedModule) {
      case AppModules.REPORTS:
        return combineLatest([reportNavigation$, reports$]).pipe(
          map(([reportNav, reps]) => toReportContext(reportNav, reps))
        );

      case AppModules.DASHBOARDS:
        return from(import('dashboard/navigation/dashboardNavigation$')).pipe(
          switchMap(({ dashboardNavigation$ }) =>
            combineLatest([dashboardNavigation$, dashboards$]).pipe(
              map(([dashNav, dashes]) => toDashboardContext(dashNav, dashes))
            )
          ),
          startWith(topLevel(AppModules.DASHBOARDS)),
          catchError(() => of(topLevel(AppModules.DASHBOARDS)))
        );

      case AppModules.VIEWPOINTS:
        return viewpointsNavigation$.pipe(map(toViewpointBrowseContext));

      case AppModules.WORKSPACES:
        return combineLatest([
          isViewpointMode$,
          loadedState$,
          traversalState$,
          activeView$,
        ]).pipe(spreadMap(toWorkspaceContext));

      case AppModules.METAMODEL:
        return combineLatest([metamodelNavigation$, metamodels$]).pipe(
          map(([mmNav, metamodels]) => toMetamodelContext(mmNav, metamodels))
        );

      case AppModules.TRANSFORMATIONS:
        return transformationsScope$.pipe(
          switchMap(toTransformationsScopeContext)
        );

      case AppModules.COMPONENT_OVERVIEW_PAGE:
        return componentOverviewPage$.pipe(map(toComponentContext));

      default:
        return of<TopLevelContext>({
          assetType: 'top_level',
          module: selectedModule as TopLevelContext['module'],
        });
    }
  }),
  distinctUntilChanged((a, b) => isEqual(a, b)),
  shareReplay({ bufferSize: 1, refCount: true })
);
